// Terms of Service and Privacy Policy, rendered by pages/Legal.tsx.
// Generic starting text: have both documents reviewed by a lawyer before charging users.
// Keep the privacy policy in step with what the app actually collects (see docs/legal.md).

export const LEGAL_EFFECTIVE_DATE = 'October 27, 2026';
export const LEGAL_CONTACT_EMAIL = 'support@leftturnbetting.com';

export const TERMS_OF_SERVICE = `
These Terms of Service ("Terms") govern your use of Left Turn Betting, including the website at
leftturnbetting.com and its subdomains and all related features (the "Service"). The Service is operated by
Left Turn Betting ("Left Turn Betting", "we", "us"). By creating an account or using the Service, you agree to these
Terms. If you don't agree, don't use the Service.

## 1. What Left Turn Betting is (and isn't)

Left Turn Betting provides motorsports statistics, race simulations, probability estimates, analysis tools and related
content for informational and entertainment purposes.

- **We are not a sportsbook.** We don't accept, place, broker or settle wagers, and we don't hold funds for betting.
- **We don't give betting, financial or investment advice.** Probabilities, "fair odds", edges, expected values, "book
  mode" prices, suggested stakes (including Kelly sizing), AI summaries and every other output are model estimates.
  They can be wrong, and past model performance doesn't guarantee future results.
- **You are solely responsible for any decision you make,** including any wager you place with a third party, and for
  any money you win or lose.

## 2. Eligibility and legal use

You must be at least **21 years old**, or the minimum age required to bet on sports where you live if that is higher,
to use the Service. You are responsible for knowing and following the laws that apply to you, including laws about
sports betting. Don't use the Service where doing so would be illegal.

## 3. Accounts

You need an account to use most features. Sign-in is provided by our authentication provider (Auth0) and may use a
third-party login such as Google. You agree to:

- give accurate information and keep your login secure;
- not share your account or let others use it;
- tell us promptly at ${LEGAL_CONTACT_EMAIL} if you think your account has been compromised.

You are responsible for activity under your account. We may suspend or close accounts that break these Terms.

## 4. Acceptable use

You agree not to:

- scrape, crawl, bulk-download or otherwise extract data or outputs from the Service by automated means, or run
  scripts against our API outside normal use of the website;
- resell, redistribute, publish or commercially exploit the Service's data, simulations, odds or other outputs
  without our written permission;
- use the Service to build or train a competing product or model;
- probe, overload or interfere with the Service, or try to get around usage limits, authentication or security;
- reverse engineer the Service, except where the law allows it;
- use the Service for anything unlawful, fraudulent or harmful, or to harass anyone.

We may limit usage (for example the number of simulations or AI requests) to keep the Service fast and affordable.

## 5. Data, content and intellectual property

The Service, including its software, models, design, articles, simulations and other outputs, is owned by us or our
licensors and protected by intellectual property laws. We give you a limited, personal, non-exclusive,
non-transferable, revocable license to use the Service for your own non-commercial purposes under these Terms.

Racing statistics shown in the Service are compiled from publicly available sources. Series, track, team and driver
names and marks belong to their respective owners. Left Turn Betting is not affiliated with, endorsed by or sponsored by
any racing series or sanctioning body, track, team, driver or sportsbook.

**Screenshots you upload.** When you upload sportsbook screenshots to the board scanner, you confirm you have the right
to share them, and you give us a non-exclusive, worldwide, royalty-free license to store and use them, and the odds
read from them, to provide and improve the Service (for example to measure and improve our models). Don't upload images
that show other people's personal information.

## 6. AI features

Some features use artificial intelligence (currently Google Gemini) to write explanations and summaries or answer
questions. AI output can be inaccurate or incomplete; check anything important. Don't enter sensitive personal
information into AI chat. What you send to AI features is processed as described in our Privacy Policy.

## 7. Paid plans

If we offer paid subscriptions, prices, billing periods and what's included will be shown before you buy, and payments
will be processed by our payment provider. Subscriptions renew automatically until canceled, and you can cancel at
any time to stop future charges. Unless required by law or stated at purchase, payments are non-refundable.

## 8. Responsible gambling

Gambling involves risk, and you can lose money. Only bet what you can afford to lose. If gambling stops being fun, or
you or someone you know has a gambling problem, help is available: call or text **1-800-GAMBLER** (US), or visit
[ncpgambling.org](https://www.ncpgambling.org/).

## 9. Disclaimers

THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE", WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS OR IMPLIED,
INCLUDING WARRANTIES OF ACCURACY, COMPLETENESS, MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND
NON-INFRINGEMENT. We don't promise that the Service, its data or its outputs will be accurate, current, complete,
uninterrupted or error-free, or that using it will lead to any particular result.

## 10. Limitation of liability

TO THE FULLEST EXTENT ALLOWED BY LAW, LEFT TURN BETTING AND ITS OWNERS AND CONTRIBUTORS WON'T BE LIABLE FOR ANY
INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL OR PUNITIVE DAMAGES, OR FOR ANY LOSS OF PROFITS, WAGERS, DATA OR GOODWILL,
ARISING FROM YOUR USE OF (OR INABILITY TO USE) THE SERVICE, INCLUDING ANY BETTING DECISION YOU MAKE. OUR TOTAL LIABILITY
FOR ANY CLAIM RELATING TO THE SERVICE IS LIMITED TO THE GREATER OF THE AMOUNT YOU PAID US IN THE 12 MONTHS BEFORE THE
CLAIM OR US$50. Some places don't allow some of these limits, so they may not all apply to you.

## 11. Indemnity

You agree to defend and hold harmless Left Turn Betting and its owners from claims, losses and expenses (including
reasonable legal fees) arising from your misuse of the Service or your breach of these Terms.

## 12. Changes and termination

We may change or discontinue any part of the Service at any time. We may update these Terms; if we make material
changes we'll update the date above and, where appropriate, tell you through the Service or by email. Continuing to use
the Service after changes take effect means you accept them. You can stop using the Service at any time, and we may
suspend or end your access if you break these Terms.

## 13. Governing law

These Terms are governed by the laws of the United States and of the state in which Left Turn Betting is based,
without regard to conflict-of-law rules. Disputes will be resolved in the state or federal courts located in that state,
unless the law where you live requires otherwise.

## 14. Contact

Questions about these Terms: ${LEGAL_CONTACT_EMAIL}.
`;

export const PRIVACY_POLICY = `
This Privacy Policy explains what information Left Turn Betting ("we", "us") collects when you use the Service, how we
use it, and the choices you have. It applies to leftturnbetting.com and its subdomains.

## 1. Information we collect

**Account information.** When you sign up or log in, our authentication provider (Auth0, by Okta) gives us your email
address and, depending on how you sign in, your name and profile picture (for example from your Google account). We
use them to identify you and show your account menu. We don't receive or store your password.

**Usage and technical information.** Like most websites, our servers and hosting provider (Google Cloud) automatically
record technical information when you use the Service, such as your IP address, browser type, the pages and features
you request, and timestamps. We use this to run, secure, debug and improve the Service.

**Usage activity.** When you're signed in, we record how you use the Service: the pages and features you open (for
example a Simulator tab or a driver's panel), the actions you take (such as running a simulation or scanning a board),
how long things take, errors, and what each request cost us to serve. It's linked to a one-way coded version of your
account ID (not your name or email). We use it to understand which features are used, keep the Service fast and
affordable, and fix problems. We don't record what you type, the odds you enter, or the questions you ask AI features
as part of this. It's kept for about 13 months and deleted when you delete your account.

**What you enter into features.** Inputs to tools (for example tracks you simulate, drivers you compare, sportsbook
odds you type into the value calculator) are processed to produce your results. Questions you send to AI features are
sent to our AI provider to generate an answer (see section 3).

**Simulation history.** When you run a simulation while signed in, we save the results with a one-way coded version
of your account ID (not your name or email) so you can reopen them under "Previous simulations", and so we can compare
our probabilities with how races turned out. They're kept for about a year.

**Slip Builder slips.** When you're signed in, the slip you build for each track (the bets, odds, stakes and books you enter)
and its latest check are saved with a one-way coded version of your account ID (not your name or email), so the slip is
there when you come back, on any device. They're kept while your account is open and deleted when you delete your
account.

**Sportsbook screenshots.** When you use the board scanner, the screenshots you upload are sent to our AI provider to
read the odds on them. We keep the odds that were read, linked to a one-way coded version of your account ID (not your
name or email), to improve our models and understand betting markets. We also keep the images themselves for up to 90
days to check how accurately they were read, unless they appear to show account details such as a balance, username or
bet slip, in which case we don't store the image. Please crop out personal details before uploading.

**Information stored in your browser.** We store a few things in your browser's local storage so the Service works
and remembers your preferences:
- your sign-in session (managed by Auth0), so you stay logged in;
- preferences such as the bankroll and Kelly fraction you enter in the value calculator. These stay on your device and
  aren't sent to our servers;
- a working copy of your Slip Builder slip, so it works offline and opens quickly (signed in, it's also saved to your
  account as described above).

We don't use advertising cookies, and we don't use third-party analytics or tracking: the usage activity above is
recorded by us, on our own servers.

**Payment information.** If we offer paid plans, payments will be handled by our payment provider (for example
Stripe). We won't see or store your full card number.

## 2. How we use information

We use the information above to:
- provide and operate the Service, including your account;
- keep the Service secure, prevent abuse and enforce our Terms (for example usage limits);
- understand and fix problems and improve features;
- respond to you and send important service messages;
- comply with legal obligations.

We do not sell your personal information, and we do not share it for cross-context behavioral advertising.

## 3. Service providers we share information with

We share information only with providers that help us run the Service, under their own terms and privacy policies:

| Provider | What it does | Information involved |
|---|---|---|
| Auth0 (Okta) | Sign-in and account security | Email, name, profile picture, login records |
| Google Cloud (Cloud Run, BigQuery, Firestore, Cloud Storage) | Hosting, databases, file storage and server logs | Technical and usage information; scanned odds, stored screenshots and saved slips |
| Google Gemini API | Writes AI explanations, summaries and chat answers, and reads uploaded screenshots | The text of AI requests, which may include what you type into AI chat, and screenshots you upload |
| Payment processor | Payments for paid plans, if offered | Billing details you give them |

We may also disclose information if required by law, to protect the rights, safety or property of us or others, or as
part of a merger, acquisition or sale of the business.

## 4. How long we keep information

We keep account information while your account is open. Server logs are kept for a limited period by our hosting
provider (generally about 30 days) and then deleted. AI providers handle request data under their own retention terms.
Uploaded screenshots are deleted after 90 days. Saved Slip Builder slips are kept while your account is open and
deleted with it. Usage activity is kept for about 13 months and deleted when you delete your account. Odds read from
screenshots are kept as market data; once your account is deleted they can no longer be linked to you. When you ask us
to delete your account, we delete or anonymize your information unless we need to keep it for legal reasons.

## 5. Your choices and rights

- **Access, correction and deletion:** you can update your account details or delete your account on your Profile
  page, or email ${LEGAL_CONTACT_EMAIL} to ask for a copy of your information or to correct or delete it.
- **Browser storage:** you can clear the Service's stored data at any time in your browser settings (this will sign
  you out and reset your preferences).
- **Location-specific rights:** depending on where you live (for example California, other US states, the EU or UK), you
  may have additional rights, such as to know what we collect, to delete it, to correct it, to object to or restrict
  certain processing, and to not be discriminated against for using these rights. We'll honor requests as the law
  requires. You may also complain to your local data protection authority.

## 6. Security

We use reasonable measures to protect information, including encrypted connections (HTTPS), access controls and
reputable providers. No system is perfectly secure, so we can't guarantee absolute security.

## 7. Children

The Service is for adults (21+, or the legal betting age where you live if higher). We don't knowingly collect
information from anyone under 18. If you think a minor has given us information, contact us and we'll delete it.

## 8. International users

We're based in the United States and our providers may process information in the US and elsewhere. By using the
Service, you understand your information may be processed outside your country.

## 9. Changes to this policy

We may update this policy. If we make material changes, we'll update the date above and, where appropriate, tell you
through the Service or by email.

## 10. Contact

Questions or requests: ${LEGAL_CONTACT_EMAIL}.
`;
